'use client';

import { Button, LoadingBar, Tooltipß } from '@otter-ai/den-components';
import {
  Building_2Icon,
  InformationIcon,
  ShieldTickIcon,
  Sparkles_2Icon,
} from '@otter-ai/den-icons';
import {
  OnboardSetupStepEvent,
  OnboardSetupStepNextEvent,
  Screen,
  trackEvent,
} from '@otter-ai/telemetry';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useFinishOnboardingStep } from '../../hooks/useFinishOnboardingStep';

export function BasicWorkspaceInfo() {
  const t = useTranslations('welcome:basic-workspace-info');
  const pathname = usePathname();
  const newOnboardingFlow = useSearchParams().get('new_onboarding');

  const { finishOnboardingStepAsync, isFinishingOnboarding } =
    useFinishOnboardingStep({
      onSuccess: () => {
        trackEvent(
          OnboardSetupStepNextEvent({
            Screen: Screen.onboardBasicWorkspaceInfo,
          }),
        );
      },
    });

  useEffect(() => {
    if (!newOnboardingFlow) return;

    trackEvent(
      OnboardSetupStepEvent({
        Screen: Screen.onboardBasicWorkspaceInfo,
        NewOnboardingFlow: newOnboardingFlow,
        OnboardPath: pathname,
      }),
    );
  }, [newOnboardingFlow]);

  return (
    <>
      {isFinishingOnboarding && (
        <LoadingBar data-testid="onboarding-loading-bar" />
      )}
      <div
        className="w-full h-dvh flex flex-row flex-nowrap"
        data-testid="app-basic-workspace-info"
      >
        <div className="flex flex-1 basis-0 flex-col items-center">
          <div className="flex-1 min-h-0 flex flex-col justify-center items-center w-full pt-[60px]">
            <div className="box-border w-full max-w-[582px] max-h-full p-6 flex flex-col overflow-y-auto space-y-8 otter-scrollbar">
              <div>
                <div className="text-sm text-muted mb-4">{t('step')}</div>
                <div className="font-semibold text-3xl">{t('title')}</div>
              </div>
              <div className="flex flex-col flex-wrap max-w-[1036px] space-y-4">
                <div className="flex gap-2">
                  <div className="pt-1">
                    <Sparkles_2Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex flex-col gap-0">
                    <div className="text-md font-semibold">
                      {t('card_1.title')}
                    </div>
                    <div className="text-subtle text-md">
                      {t('card_1.description')}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="pt-1">
                    <Building_2Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex flex-col gap-0">
                    <div className="text-md font-semibold">
                      {t('card_2.title')}
                    </div>
                    <div className="text-subtle text-md">
                      {t('card_2.description')}
                      <Tooltipß
                        data-testid="workspace-info-tooltip"
                        caption={t('card_2.tooltip')}
                        side="right"
                      >
                        <span className="inline-flex items-center align-middle cursor-pointer">
                          <InformationIcon className="h-6 w-6 text-primary" />
                        </span>
                      </Tooltipß>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="pt-1">
                    <ShieldTickIcon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex flex-col gap-0">
                    <div className="text-md font-semibold">
                      {t('card_3.title')}
                    </div>
                    <div className="text-subtle text-md">
                      {t('card_3.description')}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-start pl-8">
                <Button
                  disabled={isFinishingOnboarding}
                  className="py-3 px-[14px] w-24 h-[20px] box-content"
                  size="lg"
                  onClick={() => finishOnboardingStepAsync({})}
                  data-testid="basic-workspace-info-next-button"
                >
                  {t('button')}
                </Button>
              </div>
            </div>
          </div>
          <div className="text-center text-muted text-sm shrink-0 pb-2">
            {t('footer.prefix')}{' '}
            <a
              href="https://otter.ai/terms-of-service"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {t('footer.terms_text')}
            </a>{' '}
            {t('footer.middle')}{' '}
            <a
              href="https://otter.ai/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {t('footer.privacy_text')}
            </a>
          </div>
        </div>
        <div className="hidden lg:block box-border rounded-2xl p-4 w-[50vw]">
          {/** biome-ignore lint/performance/noImgElement: without this the style of the image is not applied */}
          <img
            src="/nextjs/images/onboarding-basic-workspace-info.png"
            alt="onboarding-basic-workspace-invite-teammate"
            className="block box-border h-full object-contain"
          />
        </div>
      </div>
    </>
  );
}
